<script lang="ts">
  import type { AppContext, MissionInfo, TimelineEvent } from '../app';
  import { isShown } from '../core/missionState';
  import { lang, t } from '../i18n';
  import { get } from 'svelte/store';
  import { currentDate, follow, focusOn, playback, setDate } from '../state/stores';

  // The event chronology (S3-16): a tap jumps to the event and flies the camera to the spacecraft (D16: the panel
  // changes the date only through such an explicit tap). The latest event before the current date is marked.
  // Maintainer feedback: the jump lands a little *before* the event — about three seconds of playback at the
  // current speed, between an hour and a day; the slow-motion brakes a flyby from there — and the camera follows the
  // spacecraft when it is in flight then.
  let { ctx, mission }: { ctx: AppContext; mission: MissionInfo } = $props();

  const current = $derived.by(() => {
    let last: TimelineEvent | null = null;
    for (const e of mission.events) if (e.time <= $currentDate) last = e;
    return last;
  });

  const fmt = (ms: number, l: string): string =>
    new Intl.DateTimeFormat(l === 'de' ? 'de-DE' : 'en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(ms);

  const HOUR = 3_600_000;
  const DAY = 24 * HOUR;
  const LEAD_PLAYBACK_S = 3;

  function go(e: TimelineEvent): void {
    const sc = e.spacecraft ?? mission.spacecraft[0];
    const lead = Math.min(DAY, Math.max(HOUR, LEAD_PLAYBACK_S * get(playback).speed * 1000));
    // a launch: the moment the spacecraft appears (the trajectory starts after the parking orbit)
    const t = e.type === 'launch' ? Math.max(e.time, (sc ? ctx.scene.flightStart(sc) : null) ?? e.time) : e.time - lead;
    setDate(t);
    if (!sc || !ctx.targets.has(sc)) return;
    const state = ctx.scene.lifeStateAt(sc, t);
    if (isShown(state ?? undefined)) follow(sc);
    else focusOn(sc);
  }
</script>

<ol class="events" aria-label={$t('panel.events')}>
  {#each mission.events as e (e.time + e.type)}
    <li class:current={e === current}>
      <button type="button" onclick={() => go(e)}>
        <span class="when">{fmt(e.time, $lang)}</span>
        <span class="what">{e.title[$lang]}</span>
      </button>
    </li>
  {/each}
</ol>

<style>
  .events {
    margin: 0;
    padding: 0;
    list-style: none;
    border-left: var(--rule-w) solid var(--rule);
  }
  li button {
    position: relative;
    width: 100%;
    min-height: var(--touch);
    padding: 4px 8px 4px 14px;
    display: grid;
    grid-template-columns: 7.5em 1fr;
    gap: 8px;
    align-items: center;
    text-align: left;
  }
  li button::before {
    content: '';
    position: absolute;
    left: -6px;
    top: 50%;
    width: 8px;
    height: 8px;
    margin-top: -4px;
    background: var(--rule);
    transform: rotate(45deg);
  }
  li.current button::before {
    background: var(--signal);
  }
  li button:hover .what {
    color: var(--accent);
  }
  .when {
    font-family: var(--font-mono);
    font-size: 0.75rem;
    color: var(--fg-muted);
  }
  li.current .when {
    color: var(--accent);
  }
  .what {
    font-size: 0.85rem;
  }
</style>
